<script>
  import { onDestroy, tick } from "svelte";

  let isOpen = false;
  let chatContainer;
  let chatInstance = null;
  let isLoading = false;
  let hasError = false;
  let errorMessage = "";
  let mounted = false;

  // ── Toggle: panel is always in DOM, so chatContainer ref is always valid.
  const toggleChat = () => {
    isOpen = !isOpen;
    if (isOpen && !mounted) {
      loadChatModule();
    }
  };

  // ── Close
  const closeChat = () => {
    isOpen = false;
  };

  // ── Load Remote Module
  const loadChatModule = async () => {
    if (mounted || isLoading) return;

    isLoading = true;
    hasError = false;

    try {
      const module = await import("App/AI-Chat");
      const ChatComponent = module.default;

      if (!ChatComponent) throw new Error("default export is undefined");

      isLoading = false;
      await tick();

      if (!chatContainer) throw new Error("chatContainer is null after tick");

      chatContainer.innerHTML = "";
      chatInstance = new ChatComponent({ target: chatContainer });
      mounted = true;
    } catch (err) {
      hasError = true;
      errorMessage = err?.message ?? "Failed to load chat module.";
      isLoading = false;
    }
  };

  // ── Retry
  const retryLoad = async () => {
    mounted = false;
    hasError = false;
    errorMessage = "";
    await tick();
    loadChatModule();
  };

  // ── Lifecycle
  onDestroy(() => {
    if (chatInstance && typeof chatInstance.$destroy === "function") {
      chatInstance.$destroy();
    }
  });
</script>

<!-- ── Floating Action Button ──────────────────────────────────── -->
<button
  id="ai-chat-toggle-btn"
  class="fixed bottom-7 right-7 z-[9999] w-14 h-14 rounded-full border-0 cursor-pointer
         flex items-center justify-center text-white
         bg-[#34344A]
         shadow-[0_4px_20px_rgba(52,52,74,0.7),0_2px_8px_rgba(0,0,0,0.3)]
         hover:bg-[#3e3e58] hover:shadow-[0_6px_28px_rgba(52,52,74,0.9)]"
  on:click={toggleChat}
  aria-label={isOpen ? "Close AI Chat" : "Open AI Chat"}
  title={isOpen ? "Close Chat" : "AI Assistant"}
>
  {#if isOpen}
    <!-- Close icon -->
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" stroke-width="2.5"
         stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  {:else}
    <!-- Chat bubble icon -->
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
    </svg>
  {/if}
</button>

<!-- ── Backdrop: always in DOM, hidden when closed ────────────── -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="fixed inset-0 z-[9997] bg-black/30 sm:hidden"
  on:click={closeChat}
  aria-hidden="true"
  style:display={isOpen ? "block" : "none"}
></div>

<!-- ── Chat Panel: always in DOM, toggled via display ─────────── -->
<div
  id="ai-chat-panel"
  role="dialog"
  aria-label="AI Chat Assistant"
  style:display={isOpen ? "grid" : "none"}
  style="height: 800px; width: 1000px; grid-template-rows: auto 1fr;"
  class="fixed bottom-24 right-7 z-[9998]
         rounded-2xl overflow-hidden
         bg-[#2a2a3e]
         shadow-[0_24px_64px_rgba(0,0,0,0.4),0_8px_24px_rgba(52,52,74,0.3),0_0_0_1px_rgba(255,255,255,0.06)]"
>
  <!-- Header -->
  <div class="flex items-center justify-between px-4 py-3
              bg-[#34344A] border-b border-white/10
              text-white flex-shrink-0">
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm
                  border border-white/30 flex items-center justify-center flex-shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83
                   M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
        </svg>
      </div>

      <div class="flex flex-col">
        <span class="font-bold text-[15px] tracking-tight leading-tight font-sans">
          AI Assistant
        </span>
        <span class="flex items-center gap-1.5 text-[11px] opacity-85 mt-0.5">
          <span class="w-1.5 h-1.5 rounded-full bg-green-400
                       shadow-[0_0_0_2px_rgba(74,222,128,0.3)] animate-pulse inline-block"></span>
          Online
        </span>
      </div>
    </div>

    <button
      class="w-8 h-8 rounded-full bg-white/15 border-0 text-white cursor-pointer
             flex items-center justify-center flex-shrink-0
             transition-colors duration-200 hover:bg-white/25"
      on:click={closeChat}
      aria-label="Close chat"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2.5"
           stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  </div>

  <!-- Body: fills the second grid row, constrains remote component height -->
  <div class="flex flex-col bg-[#2a2a3e] relative" style="min-height: 0; overflow: hidden;">
    <!-- Loading overlay -->
    {#if isLoading}
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-3
                  text-white/70 bg-[#2a2a3e] z-10">
        <div class="w-9 h-9 rounded-full border-[3px] border-white/20 border-t-white animate-spin"></div>
        <p class="text-sm font-medium font-sans">Loading AI Chat…</p>
      </div>
    {/if}

    <!-- Error overlay -->
    {#if hasError}
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-3
                  p-6 text-center bg-[#2a2a3e] z-10">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
             fill="none" stroke="#ef4444" stroke-width="1.5"
             stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p class="font-semibold text-sm text-white/90 m-0">Could not load the chat module.</p>
        <small class="text-white/50 text-[11px] break-words">{errorMessage}</small>
        <button
          class="mt-2 px-5 py-2 rounded-lg border-0 cursor-pointer
                 bg-[#34344A] border border-white/20
                 text-white text-[13px] font-semibold
                 transition-all duration-200 hover:bg-[#3e3e58]"
          on:click={retryLoad}
        >
          Try Again
        </button>
      </div>
    {/if}

    <!-- Mount target: always in DOM, fills all remaining body space -->
    <div
      id="ai-chat-mount"
      bind:this={chatContainer}
      style="flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden;"
    ></div>
  </div>
</div>

<style>
  /* Force the remote chat component to fill the mount container fully,
     so its internal flex layout can pin the input bar to the bottom. */
  :global(#ai-chat-mount > *) {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100% !important;
  }
</style>
